import { Component, computed, DestroyRef, inject, ViewChild } from '@angular/core';
import { ListCustomCell } from '@shared/modules/list/interfaces/list-custom-cell.interface';
import { Contact } from '@modules/contacts/shared/models/contact.model';
import { ContactController } from '@modules/contacts/shared/controllers/contact.controller';
import { ProjectPreviewService } from '@project-modules/project-preview/services/project-preview.service';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { ProjectController } from '@modules/projects/shared/controllers/project.controller';
import { ListService } from '@shared/modules/list/services/list.service';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { Project } from '@modules/projects/shared/models/project.model';
import { ContactAddToSystemComponent } from '@shared/components/contact-add-to-system/contact-add-to-system.component';
import { Config } from '@shared/configs/config';
import { take, tap } from 'rxjs';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { MatDialog } from '@angular/material/dialog';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'project-contact-toggle-menu',
  templateUrl: './project-contact-toggle-menu.component.html'
})
export class ProjectContactToggleMenuComponent implements ListCustomCell {
  private readonly projectStore = inject(ProjectStoreService);
  private readonly destroyRef = inject(DestroyRef);


  _row: Contact;

  readonly project = computed<Project | null>(() => this.projectStore.project() || null);

  set row(row: Contact) {
    this._row = new Contact(row);
  }

  get row() {
    return this._row;
  }

  @ViewChild('template') template;

  constructor(
    public projectPreviewService: ProjectPreviewService,
    private projectAPIService: ProjectAPIService,
    private listService: ListService,
    private s: SnackBarService,
    private t: TranslateService,
    private dialogRef: MatDialog
  ) {}

  preview() {
    const ctrl = new ContactController(this.row);
    ctrl.preview(this.row.id);
  }

  markAsClient() {
    this.projectAPIService
      .assignMainContactToProject(this.project().id, this.row.id)
      .subscribe((project: Project) => {
        this.listService.getRows();
        this.projectStore.project.set(project);
        this.s.success(this.t.instant('ProjectContacts.List.contactMarkedAsClient'));
      });
  }

  markAsResponsibleClient() {
    this.projectAPIService
      .addContactToProject(this.row.id, this.project().id, true)
      .subscribe((project: Project) => {
        this.listService.getRows();
        this.projectStore.project.set(project);
        this.s.success(this.t.instant('ProjectContacts.List.contactMarkedAsResponsible'));
      });
  }

  createTask() {
    const ctrl = new TaskSidenavController();
    ctrl.addTask({
      projectID: this.project().id,
      assignToEmails: !this.row.employee ? [this.row.email] : null,
      assignToIds: this.row.employee ? [this.row.employee.id] : null
    });
  }

  createAccount() {
    const dialog = this.dialogRef.open(ContactAddToSystemComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      autoFocus: false
    });

    dialog.componentInstance.contact = this.row;
    dialog.componentInstance.onAddContactToSystem
      .pipe(
        take(1),
        tap((contact: Contact) => {
          this.row = new Contact(contact);
          this.listService.getRows();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  removeFromProject() {
    const ctrl = new RemoveModalController();
    ctrl
      .remove(() => {
        const _ctrl = new ProjectController(this.project());
        _ctrl
          .removeContactFromProject(this.row.id, this.project().id)
          .pipe(
            tap(() => {
              this.listService.getRows();
              this.projectPreviewService.updateProject();
            })
          )
          .subscribe();
      })
      .subscribe();
  }
}
